import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CarrierServiceAreaAssignment,
  useCarrierTransportServices,
  useUpdateCarrierServiceArea,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  parseOptionalCapacity,
  serviceCoverageDrafts,
  serviceCoverageInputs,
  type ServiceCoverageDraft,
  ServiceCoverageFields,
  ServiceAreaCheckbox,
  validateServiceCoverages,
} from "@/features/service-area-form-controls";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { hasHttpStatus } from "@/lib/api-error";

interface EditServiceAreaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  areaName: string;
  platformMaxDailyOrders: number | null;
  assignment: CarrierServiceAreaAssignment;
}

export function EditServiceAreaDialog({
  open,
  onOpenChange,
  areaName,
  platformMaxDailyOrders,
  assignment,
}: EditServiceAreaDialogProps) {
  const update = useUpdateCarrierServiceArea();
  const transportServices = useCarrierTransportServices();
  const services = (transportServices.data ?? []).filter(
    (service) => service.isActive
  );
  const initialCoverage = useMemo(
    () => serviceCoverageDrafts(assignment.serviceCoverages),
    [assignment.serviceCoverages]
  );
  const [serviceCoverage, setServiceCoverage] =
    useState<Record<string, ServiceCoverageDraft>>(initialCoverage);
  const [active, setActive] = useState(assignment.active);
  const [maxDaily, setMaxDaily] = useState(
    assignment.maxDailyOrders == null ? "" : String(assignment.maxDailyOrders)
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const pending = submitting || update.isPending;
  const initialMaxDaily =
    assignment.maxDailyOrders == null ? "" : String(assignment.maxDailyOrders);
  const dirty =
    JSON.stringify(serviceCoverage) !== JSON.stringify(initialCoverage) ||
    active !== assignment.active ||
    maxDaily.trim() !== initialMaxDaily;
  const formId = `edit-service-area-${assignment.id}`;

  useUnsavedChangesGuard(
    open && dirty,
    "Leaving this page will discard your unsaved service-area changes. Continue?"
  );

  useEffect(() => {
    if (!open) return;
    setServiceCoverage(initialCoverage);
    setActive(assignment.active);
    setMaxDaily(initialMaxDaily);
    setError(null);
  }, [assignment, initialCoverage, initialMaxDaily, open]);

  const close = () => {
    if (pending) return;
    if (
      dirty &&
      !window.confirm("Discard your unsaved service-area changes?")
    ) {
      return;
    }
    onOpenChange(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submissionLock.current) return;
    setError(null);
    const capacity = parseOptionalCapacity(maxDaily);
    if (capacity.error) {
      setError(capacity.error);
      return;
    }
    const coverageError = validateServiceCoverages(services, serviceCoverage);
    if (coverageError) {
      setError(coverageError);
      return;
    }

    submissionLock.current = true;
    setSubmitting(true);
    try {
      await update.mutateAsync({
        id: assignment.id,
        patch: {
          serviceCoverages: serviceCoverageInputs(services, serviceCoverage),
          maxDailyOrders: capacity.value,
          active,
        },
      });
      toast.success("Service area updated.");
      onOpenChange(false);
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to edit service areas."
        : (response.response?.data?.error ??
          "Service area could not be updated.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
      title={`Edit ${areaName} coverage`}
      presentation="responsive-sheet"
      closeDisabled={pending}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "No unsaved changes"}
          </span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="w-full sm:w-auto"
              onClick={close}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              size="touch"
              className="w-full sm:w-auto"
              disabled={pending || !dirty}
            >
              {pending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-busy={pending || undefined}
      >
        <ServiceCoverageFields
          services={services}
          values={serviceCoverage}
          savedValues={initialCoverage}
          areaId={assignment.serviceAreaId}
          disabled={pending || transportServices.isLoading}
          onChange={(serviceId, value) => {
            setServiceCoverage((current) => ({
              ...current,
              [serviceId]: value,
            }));
            setError(null);
          }}
        />

        <ServiceAreaCheckbox
          id="sa-edit-active"
          label="Active in this service area"
          checked={active}
          disabled={pending}
          onChange={setActive}
        />

        <label htmlFor="sa-edit-cap" className="space-y-1 text-sm font-medium">
          <span>
            Max daily orders (blank = platform default
            {platformMaxDailyOrders == null
              ? ""
              : ` of ${platformMaxDailyOrders}`}
            )
          </span>
          <Input
            id="sa-edit-cap"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={maxDaily}
            onChange={(event) => {
              setMaxDaily(event.target.value);
              setError(null);
            }}
            disabled={pending}
            className="w-full sm:w-56"
          />
        </label>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
